import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  X,
  Printer,
  Download,
  Mail,
  CreditCard,
  Ship,
  Package,
  Plane,
  Truck,
  Layers,
  MapPin,
  Calendar,
  Clock,
  FileText,
  CheckCircle,
  AlertTriangle,
  Leaf,
} from "lucide-react";

const MYEUROPACIFIC_LOGO = "https://customer-assets.emergentagent.com/job_65c103ce-390f-4bae-b6d5-2798d8bc6281/artifacts/6in60tne_image.png";

// Fallback exchange rates relative to EUR
const FALLBACK_EXCHANGE_RATES = {
  EUR: 1.0, USD: 1.08, GBP: 0.86, CHF: 0.95, CNY: 7.85, JPY: 162.5, KRW: 1450, INR: 90.5,
};

const SERVICE_ICONS = {
  fcl: Ship,
  lcl: Package,
  air: Plane,
  inland: Truck,
  multimodal: Layers,
};

const PERCENT_UNITS = ["%", "%_of"];
const normUnit = (u) => String(u || "").replace(/^per_/, "").toLowerCase();
const isPercentUnit = (u) => PERCENT_UNITS.includes(normUnit(u));

// Raw amount for a component in its OWN currency (handles percentage-of-base lines)
const rawComponentAmount = (comp, comps) => {
  if (!comp) return 0;
  if (isPercentUnit(comp.unit)) {
    const base = (comps || []).find((x) => x.component_id === comp.base_line_id);
    if (!base) return 0;
    const baseAmt = (parseFloat(base.unit_price) || 0) * (parseFloat(base.quantity) || 0);
    return ((parseFloat(comp.unit_price) || 0) / 100) * baseAmt;
  }
  return (parseFloat(comp.unit_price) || 0) * (parseFloat(comp.quantity) || 0);
};


const CATEGORY_NAMES = {
  freight: "Freight Charges",
  origin_charges: "Origin Charges",
  destination_charges: "Destination Charges",
  surcharges: "Surcharges",
  inland: "Inland Transport",
  customs: "Customs & Documentation",
  insurance: "Insurance",
  warehousing: "Warehousing",
  other: "Other Charges",
};

const CATEGORY_ORDER = ["freight", "origin_charges", "destination_charges", "surcharges", "inland", "customs", "insurance", "warehousing", "other"];

// Service-specific annotations to display after each category
const CATEGORY_ANNOTATIONS = {
  freight: {
    title: "Ocean Freight Terms",
    showTemplate: true,  // Flag to show editable template values
    serviceTypes: ["fcl", "lcl"]  // Only show for these service types
  },
  air_freight: {
    title: "Air Freight Terms",
    showAirTemplate: true,
    serviceTypes: ["air"]
  },
  rail: {
    title: "Rail Transport Terms",
    excludes: [
      "Energy Surcharge – METRANS (see metrans.eu/surcharges)",
      "Demurrage and Detention from shipping line above agreed terms",
      "Inland CY storage charges",
    ],
    note: "Rate valid for general/FAK cargo. For RF or ADR/IMO additionals apply."
  },
  customs_import: {
    // Procedure 4200 (VAT deferred to another EU member state → fiscal
    // representation + monthly reporting apply).
    title: "Customs Clearance Notes (Import 4200)",
    items: [
      "(incl 2 cntr, each next 15 EUR/FCL)",
      "Incl 3 HS codes, each additional 5 EUR/HS",
      "Duty + VAT acc to MRN",
      "Use of guarantee 2.5% = ((duty+vat) x 2.5%) but min 35 EUR",
      "Monthly fiscal reporting to tax authorities – EDI sys - 65 EUR/customer (VAT/EORI #)"
    ],
    excludes: [
      "TCC and other services in Port/Airport of entry",
      "Documentary control - 25 EUR/control (if required)",
      "Physical control (Port of Koper) - 130 EUR/repositioning to inspection point + 50 EUR/assistance (if required)",
      "Stripping/stuffing as result of detailed inspection – at cost of Port/Airport of entry tariff (if required)",
      "Detailed inspection of past import and/or correction of MRN due customs notice – 170 EUR/corrected MRN (if required)",
      "Late payment of customs duties = 150 EUR p. MRN <10 days / 250 EUR p. MRN >10 days"
    ],
    subject_to: [
      "Duty",
      "VAT (if fiscal cleared is deferred to EU member country consignee)"
    ],
    requirements: [
      "KYC (Know Your Customer documentation)",
      "CIPL (Commercial Invoice & Packing List)",
      "BL (Bill of Lading)"
    ],
    fiscal_explanation: {
      title: "Fiscal Representative Monthly Reporting",
      intro: "When we act as a fiscal representative for an importer, we take on a monthly ritual with the tax authorities.",
      sections: [
        {
          heading: "Why this exists",
          content: "EU tax authorities require a monthly report of all imports linked to each EU EORI number. They use this to verify that the importer correctly declared and paid VAT on goods entering the EU."
        },
        {
          heading: "What we must report",
          content: "For every importer (each VAT/EORI number), we must submit all customs-import transactions for the month, OR a 'zero report' if they had no imports that month. Even a quiet month triggers a mandatory silence-report."
        },
        {
          heading: "How the reporting is done",
          content: "We submit data through the EDI (Electronic Data Interchange) system - a digital pipeline directly into the tax administration's system. This requires data formatting, system integration, quality checks, submission tracking, and record keeping."
        },
        {
          heading: "Why there is a monthly charge",
          content: "Each importer is treated as a separate reporting entity. The fee covers: mandatory monthly EDI submission, zero reporting when applicable, fiscal representative responsibility and liability, administrative handling, corrections, and archiving."
        }
      ]
    }
  },
  customs_import_4000: {
    // Procedure 4000 (release for free circulation / home use → VAT paid
    // locally, NO fiscal representation / monthly reporting).
    title: "Customs Clearance Notes (Import 4000)",
    items: [
      "(incl 2 cntr, each next 15 EUR/FCL)",
      "Incl 3 HS codes, each additional 5 EUR/HS",
      "Duty + VAT acc to MRN",
      "Use of guarantee 2.5% = ((duty+vat) x 2.5%) but min 35 EUR"
    ],
    excludes: [
      "TCC and other services in Port/Airport of entry",
      "Documentary control - 25 EUR/control (if required)",
      "Physical control (Port of Koper) - 130 EUR/repositioning to inspection point + 50 EUR/assistance (if required)",
      "Stripping/stuffing as result of detailed inspection – at cost of Port/Airport of entry tariff (if required)",
      "Detailed inspection of past import and/or correction of MRN due customs notice – 170 EUR/corrected MRN (if required)",
      "Late payment of customs duties = 150 EUR p. MRN <10 days / 250 EUR p. MRN >10 days"
    ],
    subject_to: [
      "Duty",
      "VAT"
    ],
    requirements: [
      "KYC (Know Your Customer documentation)",
      "CIPL (Commercial Invoice & Packing List)",
      "BL (Bill of Lading)"
    ]
  },
  customs_export: {
    title: "Export Clearance Notes",
    items: [
      "Incl 3 HS codes, each additional 5 EUR/HS"
    ],
    requirements: [
      "CIPL (Commercial Invoice & Packing List)",
      "Export license (if applicable)"
    ]
  },
  customs: {
    // Default customs - will be overridden by import/export specific
    title: "Customs Notes"
  },
  inland: {
    title: "Trucking Terms & Conditions",
    items: [
      "Multistop for C/C: 50 EUR",
      "3 hrs free, then 20 EUR/started hr",
      "After 6 hrs: layday 250 EUR/day",
      "GPS URL live tracking: 35 EUR/trip",
      "Max GW per composition: 40 tons (truck + trailer + container tare + cargo)"
    ],
    excludes: [
      "Carrier locals, TCC and customs formalities (T1 or import/export clearance)",
      "One-way trucking is subject to SS Line empty D/O approval and D/O fee if any"
    ],
    note: "Rate reflects current market conditions. We reserve the right to change rate prior to service. Please re-check rate prior to delivery."
  }
};

// Renders one category annotation block (notes / excludes / requirements /
// fiscal explanation). Extracted so the customs category can show MORE THAN
// ONE block (e.g. both 4200 and 4000 import notes) side by side.
function AnnotationContent({ annotations, offer, quoteRequest, category }) {
  if (!annotations) return null;
  return (
    <div className="text-xs space-y-2">
      {annotations.title && (
        <p className="font-semibold text-blue-800">
          {(offer.service_type === "air" || quoteRequest?.service_type === "air") && category === "freight"
            ? "Air Freight Terms"
            : annotations.title}
        </p>
      )}
      {annotations.showTemplate && (offer.service_type === "fcl" || offer.service_type === "lcl" || quoteRequest?.service_type === "fcl" || quoteRequest?.service_type === "lcl") && (
        <div className="bg-white rounded p-2 border border-blue-200 font-mono">
          <p className="text-slate-600">SSLINE: {offer.carrier || offer.ssline || "[carrier name]"}</p>
          <p className="text-slate-600">TT: {offer.transitDays || offer.transit_days || offer.transit_time || "[X]"} days</p>
          <p className="text-slate-600">TS: {(offer.directService || offer.direct_service) ? "Direct Service" : (offer.transhipment || offer.ts || "[place]")}</p>
          <p className="text-slate-600">OFR valid thru: {
            offer.ofrValidUntil || offer.ofr_valid_until
              ? new Date(offer.ofrValidUntil || offer.ofr_valid_until).toLocaleDateString('en-GB')
              : (offer.validity_date || offer.valid_until
                ? new Date(offer.validity_date || offer.valid_until).toLocaleDateString('en-GB')
                : "[validity date]")
          }</p>
          <p className="text-slate-600">Bkg subj to eq and space availability</p>
          <p className="text-slate-600">Bkg subj to cgo acceptance</p>
        </div>
      )}
      {annotations.showTemplate && (offer.service_type === "air" || quoteRequest?.service_type === "air") && (
        <div className="bg-white rounded p-2 border border-sky-200 font-mono">
          <p className="text-slate-600">AIRLINE: {offer.airCarrier || offer.carrier || "[airline name]"}</p>
          <p className="text-slate-600">TT: {offer.airTransitDays || offer.transitDays || offer.transit_days || "[X]"} days</p>
          {(offer.airWeight || offer.airCW) && (
            <>
              <p className="text-slate-600">Weight: {offer.airWeight || "[X]"} kgs</p>
              <p className="text-slate-600">CW: {offer.airCW || "[X]"} kgs</p>
              {offer.airRatePerKg && (
                <p className="text-slate-600">Rate: {offer.airRatePerKg} EUR/kg × {offer.airCW || offer.airWeight} kgs = {((parseFloat(offer.airRatePerKg) || 0) * (parseFloat(offer.airCW) || parseFloat(offer.airWeight) || 0)).toFixed(2)} EUR</p>
              )}
            </>
          )}
          <p className="text-slate-600">Bkg subj to space availability</p>
        </div>
      )}
      {annotations.template && annotations.template.length > 0 && (
        <div className="bg-white rounded p-2 border border-blue-200 font-mono">
          {annotations.template.map((item, i) => (
            <p key={i} className="text-slate-600">{item}</p>
          ))}
        </div>
      )}
      {annotations.items && annotations.items.length > 0 && (
        <ul className="text-slate-600 space-y-0.5">
          {annotations.items.map((item, i) => (
            <li key={i}>• {item}</li>
          ))}
        </ul>
      )}
      {annotations.excludes && annotations.excludes.length > 0 && (
        <div className="mt-2 pt-2 border-t border-blue-200">
          <p className="font-medium text-amber-700">[NOTE] Rate excluding:</p>
          <ul className="text-slate-600 space-y-0.5 mt-1">
            {annotations.excludes.map((item, i) => (
              <li key={i}>* {item}</li>
            ))}
          </ul>
        </div>
      )}
      {annotations.subject_to && annotations.subject_to.length > 0 && (
        <div className="mt-2 pt-2 border-t border-blue-200">
          <p className="font-medium text-slate-700">Subject to applicable:</p>
          <ul className="text-slate-600 space-y-0.5 mt-1">
            {annotations.subject_to.map((item, i) => (
              <li key={i}>• {item}</li>
            ))}
          </ul>
        </div>
      )}
      {annotations.requirements && annotations.requirements.length > 0 && (
        <div className="mt-2 pt-2 border-t border-blue-200">
          <p className="font-medium text-slate-700">For clearance we need:</p>
          <ul className="text-slate-600 space-y-0.5 mt-1">
            {annotations.requirements.map((item, i) => (
              <li key={i}>• {item}</li>
            ))}
          </ul>
        </div>
      )}
      {annotations.note && (
        <p className="mt-2 pt-2 border-t border-blue-200 text-amber-700 italic">
          {annotations.note}
        </p>
      )}
      {annotations.fiscal_explanation && (
        <div className="mt-3 pt-3 border-t border-blue-200 bg-slate-50 rounded p-3">
          <p className="font-semibold text-slate-800 mb-2">
            {annotations.fiscal_explanation.title}
          </p>
          <p className="text-slate-600 italic mb-3">
            {annotations.fiscal_explanation.intro}
          </p>
          <div className="space-y-2">
            {annotations.fiscal_explanation.sections.map((section, i) => (
              <div key={i}>
                <p className="font-medium text-slate-700">{section.heading}</p>
                <p className="text-slate-600">{section.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function OfferPreviewPage({ offer, quoteRequest, onClose, onSend }) {
  const [sending, setSending] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [paymentLink, setPaymentLink] = useState(offer?.stripe_payment_link_url || "");
  const [exchangeRates, setExchangeRates] = useState(FALLBACK_EXCHANGE_RATES);
  
  // Fetch live exchange rates
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');
        if (response.ok) {
          const data = await response.json();
          setExchangeRates({
            EUR: 1.0,
            USD: data.rates.USD || FALLBACK_EXCHANGE_RATES.USD,
            GBP: data.rates.GBP || FALLBACK_EXCHANGE_RATES.GBP,
            CHF: data.rates.CHF || FALLBACK_EXCHANGE_RATES.CHF,
            CNY: data.rates.CNY || FALLBACK_EXCHANGE_RATES.CNY,
            JPY: data.rates.JPY || FALLBACK_EXCHANGE_RATES.JPY,
            KRW: data.rates.KRW || FALLBACK_EXCHANGE_RATES.KRW,
            INR: data.rates.INR || FALLBACK_EXCHANGE_RATES.INR,
          });
        }
      } catch (error) {
        console.error("Failed to fetch exchange rates:", error);
      }
    };
    fetchExchangeRates();
  }, []);
  
  // Currency conversion function
  const convertCurrency = (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;
    const amountInEUR = amount / (exchangeRates[fromCurrency] || 1);
    return amountInEUR * (exchangeRates[toCurrency] || 1);
  };
  
  if (!offer || !quoteRequest) return null;
  
  const displayCurrency = offer.currency || "EUR";
  const ServiceIcon = SERVICE_ICONS[quoteRequest.service_type] || Package;
  
  // Check if there are mixed currencies that need conversion
  const hasMixedCurrencies = (offer.components || []).some(
    c => c.currency && c.currency !== displayCurrency && c.is_included
  );
  
  // Group components by category
  const groupedComponents = {};
  (offer.components || []).forEach(comp => {
    if (comp.is_included) {
      const cat = comp.category || "other";
      if (!groupedComponents[cat]) groupedComponents[cat] = [];
      groupedComponents[cat].push(comp);
    }
  });
  
  // Calculate subtotals per category WITH currency conversion
  const categoryTotals = {};
  Object.entries(groupedComponents).forEach(([cat, comps]) => {
    categoryTotals[cat] = comps.reduce((sum, c) => {
      const componentCurrency = c.currency || displayCurrency;
      const amount = rawComponentAmount(c, offer.components || []);
      return sum + convertCurrency(amount, componentCurrency, displayCurrency);
    }, 0);
  });
  
  const grandTotal = Object.values(categoryTotals).reduce((sum, t) => sum + t, 0);
  
  const handlePrint = () => {
    window.print();
  };
  
  const handleDownloadPDF = async () => {
    if (offer.id === "preview") {
      // For preview mode, use browser print to PDF
      toast.info("Please save the offer first to download PDF, or use browser Print (Ctrl+P)");
      window.print();
      return;
    }
    
    // Check if offer has components - warn user if empty
    if (!offer.components || offer.components.length === 0) {
      toast.warning("This offer has no pricing components. Please edit and save with components first.");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/pricing/offers/${offer.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Europacific_Quote_${offer.offer_number || 'offer'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download PDF:", error);
      toast.error("Failed to download PDF");
    }
  };
  
  const handleSend = async () => {
    setSending(true);
    try {
      await onSend();
    } finally {
      setSending(false);
    }
  };

  const handleGeneratePaymentLink = async () => {
    if (!offer?.id) {
      toast.warning("Please save the offer first.");
      return;
    }
    setGeneratingLink(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/offers/${offer.id}/payment-link`,
        { expires_in_days: 30 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = res.data.payment_link_url;
      setPaymentLink(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Stripe Payment Link created & copied to clipboard");
      } catch {
        toast.success("Stripe Payment Link created");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create payment link");
    } finally {
      setGeneratingLink(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-4 print:p-0 print:bg-white"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 my-4 print:shadow-none print:rounded-none print:m-0 print:max-w-none"
        onClick={e => e.stopPropagation()}
      >
        {/* Action Bar - Hidden in print */}
        <div className="flex items-center justify-between px-6 py-3 border-b bg-slate-50 print:hidden">
          <h2 className="font-semibold text-slate-900">Offer Preview</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            {offer.payment_status !== "paid" && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleGeneratePaymentLink}
                disabled={generatingLink}
                data-testid="offer-payment-link-btn"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {generatingLink ? "Creating…" : paymentLink ? "Copy Pay Link" : "Payment Link"}
              </Button>
            )}
            {offer.status === "draft" && (
              <Button 
                size="sm" 
                onClick={handleSend}
                disabled={sending}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Mail className="h-4 w-4 mr-2" />
                {sending ? "Sending..." : "Send to Customer"}
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Printable Content */}
        <div className="p-8 print:p-6" id="offer-print-content">
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <img src={MYEUROPACIFIC_LOGO} alt="myEuropacific" className="h-12 mb-2" />
              <p className="text-sm text-slate-500">Europacific Logistika d.o.o.</p>
              <p className="text-sm text-slate-500">Ankaranska cesta 5, 6000 Koper, Slovenia</p>
            </div>
            <div className="text-right">
              <h1 className="text-2xl font-bold text-slate-900">QUOTATION</h1>
              <p className="text-lg font-semibold text-blue-600 mt-1">{offer.offer_number}</p>
              <p className="text-sm text-slate-500 mt-2">Date: {formatDate(offer.created_at)}</p>
              <p className="text-sm text-slate-500">Valid Until: {formatDate(offer.valid_until)}</p>
            </div>
          </div>

          {/* Customer & Reference */}
          <div className="grid grid-cols-2 gap-8 mb-8 p-4 bg-slate-50 rounded-lg">
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Customer</h3>
              <p className="font-semibold text-slate-900">{quoteRequest.company_name}</p>
              <p className="text-sm text-slate-600">{quoteRequest.contact_name}</p>
              <p className="text-sm text-slate-600">{quoteRequest.email}</p>
              {quoteRequest.phone && <p className="text-sm text-slate-600">{quoteRequest.phone}</p>}
            </div>
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Reference</h3>
              <p className="text-sm"><span className="text-slate-500">Quote Request:</span> <span className="font-medium">{quoteRequest.quote_number}</span></p>
              <p className="text-sm"><span className="text-slate-500">Service Type:</span> <span className="font-medium uppercase">{quoteRequest.service_type}</span></p>
              {quoteRequest.incoterm && <p className="text-sm"><span className="text-slate-500">Incoterm:</span> <span className="font-medium">{offer.incoterm || quoteRequest.incoterm}</span></p>}
            </div>
          </div>

          {/* Routing */}
          <div className="mb-8 p-4 border rounded-lg">
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Routing Information
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="font-semibold text-slate-900 flex items-center gap-2">
                  <ServiceIcon className="h-5 w-5 text-blue-600" />
                  Origin
                </p>
                <p className="text-lg font-medium text-slate-800 mt-1">{quoteRequest.origin_city}, {quoteRequest.origin_country}</p>
                {quoteRequest.origin_port_airport && <p className="text-sm text-slate-500">Port/Airport: {quoteRequest.origin_port_airport}</p>}
                {quoteRequest.origin_address && <p className="text-sm text-slate-500">{quoteRequest.origin_address}</p>}
              </div>
              <div className="px-8">
                <div className="flex items-center gap-2 text-slate-400">
                  <div className="w-8 h-0.5 bg-slate-300" />
                  <ServiceIcon className="h-6 w-6" />
                  <div className="w-8 h-0.5 bg-slate-300" />
                </div>
                {offer.transit_time && <p className="text-xs text-slate-500 text-center mt-1">{offer.transit_time}</p>}
              </div>
              <div className="flex-1 text-right">
                <p className="font-semibold text-slate-900">Destination</p>
                <p className="text-lg font-medium text-slate-800 mt-1">{quoteRequest.destination_city}, {quoteRequest.destination_country}</p>
                {quoteRequest.destination_port_airport && <p className="text-sm text-slate-500">Port/Airport: {quoteRequest.destination_port_airport}</p>}
                {quoteRequest.destination_address && <p className="text-sm text-slate-500">{quoteRequest.destination_address}</p>}
              </div>
            </div>
            {offer.routing && (
              <p className="text-sm text-slate-600 mt-3 pt-3 border-t">
                <span className="font-medium">Routing:</span> {offer.routing}
              </p>
            )}
          </div>

          {/* Cargo Details */}
          {(quoteRequest.container_type || quoteRequest.cargo_items?.length > 0 || offer.airWeight || offer.airCW || offer.cargo || offer.cargo_value ||
            (offer.service_type === 'air' || quoteRequest.service_type === 'air')) && (
            <div className="mb-8 p-4 border rounded-lg">
              <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3 flex items-center gap-2">
                <Package className="h-4 w-4" />
                Cargo Details
              </h3>
              <div className="grid grid-cols-4 gap-4 text-sm">
                {/* Air Cargo Details - show for air shipments */}
                {(offer.service_type === 'air' || quoteRequest.service_type === 'air') ? (
                  <>
                    {(offer.airWeight || quoteRequest.total_weight_kg) && (
                      <div>
                        <p className="text-slate-500">Weight</p>
                        <p className="font-medium">{offer.airWeight || quoteRequest.total_weight_kg} kg</p>
                      </div>
                    )}
                    {(offer.airVolume || quoteRequest.total_volume_cbm) && (
                      <div>
                        <p className="text-slate-500">Volume</p>
                        <p className="font-medium">{offer.airVolume || quoteRequest.total_volume_cbm} CBM</p>
                      </div>
                    )}
                    {offer.airCW && (
                      <div>
                        <p className="text-slate-500">Chargeable Weight (CW)</p>
                        <p className="font-medium">{offer.airCW} kg</p>
                      </div>
                    )}
                    {offer.airRatePerKg && (
                      <div>
                        <p className="text-slate-500">Rate per kg</p>
                        <p className="font-medium">{offer.airRatePerKg} EUR/kg</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Ocean Cargo Details - Container info */}
                    {quoteRequest.container_type && (
                      <>
                        <div>
                          <p className="text-slate-500">Container Type</p>
                          <p className="font-medium">{quoteRequest.container_type}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Quantity</p>
                          <p className="font-medium">{quoteRequest.container_quantity || 1}</p>
                        </div>
                      </>
                    )}
                    {quoteRequest.total_weight_kg && (
                      <div>
                        <p className="text-slate-500">Total Weight</p>
                        <p className="font-medium">{quoteRequest.total_weight_kg.toLocaleString()} kg</p>
                      </div>
                    )}
                    {quoteRequest.total_volume_cbm && (
                      <div>
                        <p className="text-slate-500">Total Volume</p>
                        <p className="font-medium">{quoteRequest.total_volume_cbm.toFixed(2)} CBM</p>
                      </div>
                    )}
                  </>
                )}
                {quoteRequest.commodity && (
                  <div className="col-span-2">
                    <p className="text-slate-500">Commodity</p>
                    <p className="font-medium">{quoteRequest.commodity}</p>
                  </div>
                )}
                {quoteRequest.cargo_ready_date && (
                  <div>
                    <p className="text-slate-500">Cargo Ready Date</p>
                    <p className="font-medium">{formatDate(quoteRequest.cargo_ready_date)}</p>
                  </div>
                )}
                {/* Computed cargo metrics (W/M, CBM, CW, value) */}
                {(offer.total_weight_kg || offer.cargo?.weight_kg) > 0 && !((offer.service_type === 'air' || quoteRequest.service_type === 'air') && offer.airWeight) && (
                  <div>
                    <p className="text-slate-500">Gross Weight</p>
                    <p className="font-medium">{Number(offer.total_weight_kg || offer.cargo?.weight_kg).toLocaleString()} kg</p>
                  </div>
                )}
                {offer.cargo?.computed_cbm > 0 && (
                  <div>
                    <p className="text-slate-500">Total CBM</p>
                    <p className="font-medium">{Number(offer.cargo.computed_cbm).toFixed(3)} CBM</p>
                  </div>
                )}
                {offer.cargo?.computed_wm > 0 && (
                  <div>
                    <p className="text-slate-500">W/M (chargeable)</p>
                    <p className="font-medium">{Number(offer.cargo.computed_wm).toFixed(3)}</p>
                  </div>
                )}
                {(offer.service_type === 'air' || quoteRequest.service_type === 'air') && offer.cargo?.computed_cw > 0 && !offer.airCW && (
                  <div>
                    <p className="text-slate-500">Chargeable Weight (CW)</p>
                    <p className="font-medium">{Number(offer.cargo.computed_cw).toFixed(2)} kg</p>
                  </div>
                )}
                {(offer.cargo_value || offer.cargo?.value) > 0 && (
                  <div>
                    <p className="text-slate-500">Cargo Value</p>
                    <p className="font-medium">{Number(offer.cargo_value || offer.cargo?.value).toLocaleString()} {offer.cargo_value_currency || offer.cargo?.value_currency || 'EUR'}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pricing Table */}
          <div className="mb-8">
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Detailed Pricing
            </h3>
            
            <table className="w-full text-sm border-collapse" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left p-3 font-semibold text-slate-700 border" style={{ width: '45%' }}>Description</th>
                  <th className="text-center p-3 font-semibold text-slate-700 border" style={{ width: '10%' }}>Qty</th>
                  <th className="text-center p-3 font-semibold text-slate-700 border" style={{ width: '12%' }}>Unit</th>
                  <th className="text-right p-3 font-semibold text-slate-700 border" style={{ width: '15%' }}>Rate</th>
                  <th className="text-right p-3 font-semibold text-slate-700 border" style={{ width: '18%' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Track shown annotation types to avoid duplicates
                  const shownAnnotationTypes = new Set();
                  
                  return CATEGORY_ORDER.map(category => {
                  const comps = groupedComponents[category];
                  if (!comps || comps.length === 0) return null;
                  
                  // Determine the correct annotations based on category and components
                  let annotations = CATEGORY_ANNOTATIONS[category];
                  let annotationType = category;
                  let extraAnnotations = [];  // extra blocks (e.g. 4200 + 4000 shown separately)
                  
                  // For customs category, pick import (4200/4000) or export notes.
                  if (category === "customs") {
                    const hasExportClearance = comps.some(c => 
                      c.name?.toLowerCase().includes('export') || 
                      c.name?.toLowerCase().includes('exp ')
                    );
                    const hasImportClearance = comps.some(c => 
                      c.name?.toLowerCase().includes('import') || 
                      c.name?.toLowerCase().includes('imp ') ||
                      c.name?.toLowerCase().includes('destination clearance')
                    );
                    const has4200 = comps.some(c => (c.name || '').includes('4200'));
                    const has4000 = comps.some(c => (c.name || '').includes('4000'));

                    if (has4200 || has4000) {
                      // Procedure-specific import notes. If BOTH are present,
                      // show each block separately (4200 first, then 4000).
                      const blocks = [];
                      if (has4200) blocks.push(CATEGORY_ANNOTATIONS.customs_import);       // full (fiscal rep)
                      if (has4000) blocks.push(CATEGORY_ANNOTATIONS.customs_import_4000);  // no fiscal rep
                      annotations = blocks[0];
                      extraAnnotations = blocks.slice(1);
                      annotationType = "customs_import";
                    } else if (hasExportClearance && !hasImportClearance) {
                      annotations = CATEGORY_ANNOTATIONS.customs_export;
                      annotationType = "customs_export";
                    } else if (hasImportClearance) {
                      annotations = CATEGORY_ANNOTATIONS.customs_import;
                      annotationType = "customs_import";
                    }
                    // If neither, use default minimal customs annotations
                  }
                  
                  // Check if any component in ANY category is rail-related
                  const hasRailComponent = comps.some(c => 
                    c.name?.toLowerCase().includes('rail') || 
                    c.name?.toLowerCase().includes('metrans') ||
                    c.name?.toLowerCase().includes('intermodal')
                  );
                  
                  // For freight category, check service type and components
                  const serviceType = offer.service_type || quoteRequest?.service_type;
                  if (category === "freight") {
                    if (serviceType === 'rail' || serviceType === 'rail_truck' || hasRailComponent) {
                      // Show rail terms
                      annotations = CATEGORY_ANNOTATIONS.rail;
                      annotationType = "rail";
                    } else if (serviceType === 'inland') {
                      // Show trucking terms for inland transport
                      annotations = CATEGORY_ANNOTATIONS.inland;
                      annotationType = "inland";
                    }
                    // Air freight is handled by the template render logic
                  }
                  
                  // For destination_charges, inland, or origin_charges - check for rail components
                  if (category === "destination_charges" || category === "inland" || category === "origin_charges") {
                    if (hasRailComponent && !shownAnnotationTypes.has("rail")) {
                      annotations = CATEGORY_ANNOTATIONS.rail;
                      annotationType = "rail";
                    }
                  }
                  
                  // Skip if this annotation type was already shown
                  const skipAnnotation = shownAnnotationTypes.has(annotationType);
                  if (!skipAnnotation && annotations) {
                    shownAnnotationTypes.add(annotationType);
                  }
                  
                  // For inland category, skip if trucking already shown
                  if (category === "inland" && shownAnnotationTypes.has("inland") && annotationType === "inland") {
                    annotations = null; // Don't show duplicate
                  }
                  
                  return (
                    <React.Fragment key={category}>
                      {/* Category Header */}
                      <tr className="bg-slate-50">
                        <td colSpan="5" className="p-2 font-semibold text-slate-700 border text-sm">
                          {CATEGORY_NAMES[category] || category}
                        </td>
                      </tr>
                      
                      {/* Components */}
                      {comps.map((comp, idx) => {
                        const componentCurrency = comp.currency || displayCurrency;
                        const pct = isPercentUnit(comp.unit);
                        const baseLine = pct ? (offer.components || []).find(x => x.component_id === comp.base_line_id) : null;
                        const originalAmount = rawComponentAmount(comp, offer.components || []);
                        const needsConversion = componentCurrency !== displayCurrency;
                        const convertedAmount = needsConversion 
                          ? convertCurrency(originalAmount, componentCurrency, displayCurrency) 
                          : originalAmount;
                        
                        return (
                          <tr key={comp.component_id || idx} className="hover:bg-slate-50">
                            <td className="p-2 border align-top">
                              <p className="font-medium text-slate-800">{comp.name}</p>
                              {comp.description && <p className="text-xs text-slate-600 mt-0.5">{comp.description}</p>}
                              {pct && baseLine && <p className="text-xs text-amber-700 mt-0.5">{comp.unit_price}% of {baseLine.name}</p>}
                              {comp.notes && <p className="text-xs text-slate-500 mt-0.5 italic">{comp.notes}</p>}
                            </td>
                            <td className="p-2 border text-center text-slate-600 align-top">{pct ? "—" : comp.quantity}</td>
                            <td className="p-2 border text-center text-slate-600 text-xs align-top">
                              {pct ? `${comp.unit_price}%` : (comp.unit?.replace("per_", "/") || "unit")}
                            </td>
                            <td className="p-2 border text-right text-slate-600 align-top font-mono">
                              {pct ? "—" : `${comp.unit_price?.toFixed(2)} ${componentCurrency}`}
                            </td>
                            <td className="p-2 border text-right font-medium text-slate-800 align-top font-mono">
                              {needsConversion ? (
                                <div>
                                  <span className="line-through text-slate-400 text-xs mr-1">
                                    {originalAmount.toFixed(2)} {componentCurrency}
                                  </span>
                                  <br />
                                  <span className="text-blue-700">
                                    {convertedAmount.toFixed(2)} {displayCurrency}
                                  </span>
                                </div>
                              ) : (
                                <span>{originalAmount.toFixed(2)} {displayCurrency}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      
                      {/* Category Subtotal */}
                      <tr className="bg-slate-50">
                        <td colSpan="4" className="p-2 border text-right font-medium text-slate-600 text-xs uppercase">
                          Subtotal {CATEGORY_NAMES[category]}
                        </td>
                        <td className="p-2 border text-right font-semibold text-slate-700 font-mono">
                          {categoryTotals[category]?.toFixed(2)} {offer.currency}
                        </td>
                      </tr>
                      
                      {/* Category Annotations */}
                      {annotations && (
                        <tr>
                          <td colSpan="5" className="p-3 border bg-blue-50">
                            <AnnotationContent annotations={annotations} offer={offer} quoteRequest={quoteRequest} category={category} />
                          </td>
                        </tr>
                      )}
                      {extraAnnotations.map((extraAnn, bi) => (
                        <tr key={`extra-ann-${category}-${bi}`}>
                          <td colSpan="5" className="p-3 border bg-blue-50">
                            <AnnotationContent annotations={extraAnn} offer={offer} quoteRequest={quoteRequest} category={category} />
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                });
                })()}
              </tbody>
            </table>
          </div>

          {/* Summary Box */}
          <div className="mb-8 p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl text-white">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  Quotation Summary
                </h3>
                <div className="space-y-2 text-sm">
                  {Object.entries(categoryTotals).map(([cat, total]) => (
                    <div key={cat} className="flex justify-between gap-8">
                      <span className="text-slate-400">{CATEGORY_NAMES[cat] || cat}:</span>
                      <span className="font-medium">{total.toFixed(2)} {displayCurrency}</span>
                    </div>
                  ))}
                </div>
                {hasMixedCurrencies && (
                  <div className="mt-4 pt-3 border-t border-slate-600 text-xs text-amber-400 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>Amounts converted to {displayCurrency} using live exchange rates. Final invoice may vary due to rate fluctuations.</span>
                  </div>
                )}
              </div>
              <div className="text-right">
                <p className="text-slate-400 text-sm uppercase">Total Amount</p>
                <p className="text-4xl font-bold mt-1">
                  {grandTotal.toFixed(2)}
                  <span className="text-xl ml-2 font-normal text-slate-300">{displayCurrency}</span>
                </p>
                <p className="text-slate-400 text-xs mt-2">
                  Valid until {formatDate(offer.valid_until)}
                </p>
              </div>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div className="mb-8 p-4 border rounded-lg">
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3">Terms & Conditions</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Incoterm</p>
                <p className="font-medium">{offer.incoterm || "As per agreement"}</p>
              </div>
              <div>
                <p className="text-slate-500">Payment Terms</p>
                <p className="font-medium">{offer.payment_terms || "30 days from B/L date"}</p>
              </div>
              {offer.transit_time && (
                <div>
                  <p className="text-slate-500">Transit Time</p>
                  <p className="font-medium">{offer.transit_time}</p>
                </div>
              )}
              <div>
                <p className="text-slate-500">Validity</p>
                <p className="font-medium">{offer.validity_days} days from quotation date</p>
              </div>
            </div>
            {offer.special_conditions && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-slate-500 text-xs uppercase mb-1">Special Conditions</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{offer.special_conditions}</p>
              </div>
            )}
          </div>

          {/* Comment Sections (from offer builder) */}
          {offer.comment_sections && offer.comment_sections.some(s => s.text || (s.images && s.images.length > 0)) && (
            <div className="mb-8">
              {offer.comment_sections.filter(s => s.text || (s.images && s.images.length > 0)).map((section) => (
                <div key={section.id} className="mb-4 p-4 border rounded-lg bg-slate-50">
                  <h4 className="text-xs font-semibold text-slate-600 uppercase mb-2">{section.title || `Comment ${section.id}`}</h4>
                  {section.text && (
                    <p className="text-sm text-slate-700 whitespace-pre-wrap mb-2">{section.text}</p>
                  )}
                  {section.images && section.images.length > 0 && (
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      {section.images.map((img) => (
                        <div key={img.id} className="rounded-lg overflow-hidden border border-slate-200">
                          <img 
                            src={img.url?.startsWith('/api') ? `${API_BASE_URL}${img.url}` : img.url} 
                            alt={img.name || 'Attachment'} 
                            className="w-full h-auto max-h-48 object-contain bg-white"
                          />
                          {img.name && (
                            <p className="text-xs text-slate-500 p-1 text-center bg-slate-50 border-t">{img.name}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* CO2 Emissions Section */}
          {offer.co2_emissions && offer.co2_emissions.co2_kg && (
            <div className="mb-8 p-4 border border-green-200 rounded-lg bg-green-50">
              <h3 className="text-xs font-semibold text-green-700 uppercase mb-3 flex items-center gap-2">
                <Leaf className="h-4 w-4" />
                Environmental Impact
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-green-600">CO2 Emissions</p>
                  <p className="font-semibold text-green-800">
                    {offer.co2_emissions.co2_kg?.toLocaleString(undefined, { maximumFractionDigits: 2 })} kg
                  </p>
                </div>
                <div>
                  <p className="text-green-600">In Tons</p>
                  <p className="font-semibold text-green-800">
                    {offer.co2_emissions.co2_tons?.toFixed(4)} t
                  </p>
                </div>
                {offer.co2_emissions.distance_km && (
                  <div>
                    <p className="text-green-600">Est. Distance</p>
                    <p className="font-semibold text-green-800">
                      {offer.co2_emissions.distance_km?.toLocaleString(undefined, { maximumFractionDigits: 0 })} km
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-green-600">Carbon Offset Cost</p>
                  <p className="font-semibold text-green-800">
                    ${offer.co2_emissions.carbon_offset_usd?.toFixed(2)} USD
                  </p>
                </div>
              </div>
              <p className="text-xs text-green-600 mt-3 italic">
                Based on GLEC Framework methodology. Contact us for carbon offset options.
              </p>
            </div>
          )}

          {/* Standard Terms */}
          <div className="text-xs text-slate-500 mb-8">
            <h3 className="font-semibold text-slate-600 uppercase mb-2">General Terms</h3>
            <ul className="list-disc pl-4 space-y-1">
              <li>Rates are subject to space and equipment availability at time of booking.</li>
              <li>Transit times are indicative and subject to carrier schedule changes.</li>
              <li>All rates exclude customs duties, taxes, and government fees unless stated.</li>
              <li>Quotation is based on the cargo details provided. Changes may affect pricing.</li>
              <li>Demurrage and detention charges are for the account of the shipper/consignee.</li>
              <li>Insurance is not included unless specifically mentioned in this quotation.</li>
              <li className="text-amber-600 font-medium">Currency conversions are based on indicative exchange rates at time of quotation. Final invoice amounts may vary due to rate fluctuations and bank exchange rates applied during invoicing.</li>
            </ul>
          </div>

          {/* Footer */}
          <div className="pt-6 border-t text-center">
            <p className="text-sm text-slate-600">
              To accept this quotation, please reply to this email or book through your Europacific portal.
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Questions? Contact us at <span className="text-blue-600">info@europacific.si</span>
            </p>
            <div className="mt-6 pt-4 border-t text-xs text-slate-400">
              <p>Europacific Logistika d.o.o. • Ankaranska cesta 5, 6000 Koper, Slovenia</p>
              <p>Tel: +386-5-6626-380 • www.europacific.si</p>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #offer-print-content, #offer-print-content * {
            visibility: visible;
          }
          #offer-print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </motion.div>
  );
}
